import { findChatById } from '@/lib/chatIndex';
import React, { useMemo, useRef, useState } from 'react';
import { Linking, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import { useAuthStore, useChatStore, useToastStore } from '@/store';
import useExperienceProgress from '@/hooks/useExperienceProgress';
import { experienceItemKey } from '@/constants/utils/experience_spec';
import CardActions from './CardActions';
import { getGuideColors, GuideHeader, GuideSection, GuideSummary, GuideTips, guideStyles } from './GuideCardLayout';

export const EXPERIENCE_PRESENTATION = {
  mealplan: { label: 'Meal plan', icon: 'utensils', section: 'Your week', light: '#935b24', dark: '#e1ad70' },
  checklist: { label: 'Checklist', icon: 'list-check', section: 'The essentials', light: '#456e57', dark: '#9fc9ad' },
  itinerary: { label: 'Itinerary', icon: 'map-location-dot', section: 'Your itinerary', light: '#365f99', dark: '#9bbcec' },
  comparison: { label: 'Comparison', icon: 'scale-balanced', section: 'Your options', light: '#6d5696', dark: '#c2afe5' },
  studyplan: { label: 'Study plan', icon: 'book-open', section: 'Your learning path', light: '#6d5696', dark: '#c2afe5' },
  projectguide: { label: 'Project guide', icon: 'hammer', section: 'Step by step', light: '#935b24', dark: '#e1ad70' },
  eventplan: { label: 'Event plan', icon: 'calendar', section: 'The plan', light: '#94556b', dark: '#dfabc0' },
};

// React Native Web's Pressable only handles Space for the button role.
// Checkbox/radio/tab roles need Space and grouped arrow-key behavior explicitly.
function selectionKeys(event, select, index, count, role) {
  if (event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault();
    if (!event.repeat) select(index);
    return;
  }
  if (!count) return;
  const delta = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 0;
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : delta ? (index + delta + count) % count : null;
  if (next === null) return;
  event.preventDefault();
  select(next);
  event.currentTarget.parentElement?.querySelectorAll(`[role="${role}"]`)[next]?.focus();
}

function SmallButton({ label, onPress, colors, expanded, icon }) {
  return (
    <Pressable accessibilityRole="button" aria-expanded={expanded} accessibilityLabel={label} accessibilityState={expanded === undefined ? undefined : { expanded }} onPress={onPress}
      style={({ pressed, hovered }) => [styles.smallButton, { backgroundColor: hovered ? colors.band : 'transparent', opacity: pressed ? 0.65 : 1 }]}>
      {!!icon && <Icon name={icon} size={12} color={colors.accent} />}
      <Text style={[styles.smallButtonText, { color: colors.accent }]}>{label}</Text>
    </Pressable>
  );
}

function CheckRow({ item, colors, progress }) {
  const checked = !!progress.checked[experienceItemKey(item)];
  // On Mac, keep the text outside the click target so dragging selects it
  // without toggling the checklist or making the whole row an accessibility group.
  const Row = Platform.OS === 'macos' ? View : Pressable;
  const Checkbox = Platform.OS === 'macos' ? Pressable : View;
  const toggleProps = {
    accessibilityRole: 'checkbox', accessibilityLabel: item.title,
    accessibilityState: { checked, disabled: !progress.ready },
    'aria-checked': checked, 'aria-disabled': !progress.ready,
    disabled: !progress.ready, onPress: () => progress.toggle(item),
  };
  return (
    <Row {...(Platform.OS === 'macos' ? { accessible: false } : toggleProps)}
      {...(Platform.OS === 'web' && { onKeyDown: event => { if (progress.ready) selectionKeys(event, () => progress.toggle(item), 0); } })}
      style={Platform.OS === 'macos' ? [styles.checkRow, { borderColor: colors.border }] : ({ pressed }) => [styles.checkRow, { borderColor: colors.border, opacity: pressed ? 0.65 : 1 }]}>
      <Checkbox {...(Platform.OS === 'macos' && { ...toggleProps, hitSlop: 10 })} style={[styles.checkbox, { borderColor: checked ? colors.accent : colors.secondary, backgroundColor: checked ? colors.accent : 'transparent' }]}>
        {checked && <Icon name="check" size={11} color={colors.background} />}
      </Checkbox>
      <View style={styles.rowBody}>
        <Text selectable style={[styles.rowTitle, { color: checked ? colors.secondary : colors.primary, textDecorationLine: checked ? 'line-through' : 'none' }]}>{item.title}</Text>
        {!!item.detail && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{item.detail}</Text>}
        {!!item.time && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{item.time}</Text>}
        {!!item.owner && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>Owner: {item.owner}</Text>}
      </View>
    </Row>
  );
}

function RecipeDetail({ recipe, colors }) {
  return (
    <View style={[styles.recipeDetail, { backgroundColor: colors.band }]}>
      <Text selectable style={[styles.rowTitle, { color: colors.primary }]}>{recipe.title}</Text>
      <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{[recipe.totalTime, recipe.servings && `${recipe.servings} servings`].filter(Boolean).join(' · ')}</Text>
      <GuideSection title="Ingredients" colors={colors} />
      {recipe.ingredientGroups.map((group, index) => <View key={index}>
        {!!group.section && <Text selectable style={[guideStyles.groupTitle, { color: colors.secondary }]}>{group.section}</Text>}
        {group.items.map((ingredient, i) => <Text selectable key={i} style={[styles.recipeLine, { color: colors.primary }]}>• {ingredient}</Text>)}
      </View>)}
      <GuideSection title="Instructions" colors={colors} />
      {recipe.steps.map((step, i) => <Text selectable key={i} style={[styles.recipeLine, { color: colors.primary }]}>{i + 1}. {step}</Text>)}
      {recipe.tips.map((tip, i) => <Text selectable key={i} style={[styles.recipeLine, { color: colors.secondary }]}>{tip}</Text>)}
    </View>
  );
}

function PlanRow({ item, index, kind, colors, openLink }) {
  const [expanded, setExpanded] = useState(false);
  const timeline = kind === 'itinerary' || kind === 'eventplan';
  return (
    <View style={[styles.planRow, { borderColor: colors.border }]}>
      <View style={styles.rowMain}>
        {timeline ? <View style={styles.timeColumn}>
          <Text selectable style={[styles.time, { color: colors.accent }]}>{item.time || String(index + 1).padStart(2, '0')}</Text>
          <View style={[styles.timelineLine, { backgroundColor: colors.border }]} />
        </View> : <View style={[guideStyles.number, { backgroundColor: colors.band }]}>
          <Text selectable style={[guideStyles.numberText, { color: colors.accent }]}>{index + 1}</Text>
        </View>}
        <View style={styles.rowBody}>
          {!timeline && !!item.time && <Text selectable style={[styles.mealLabel, { color: colors.accent }]}>{item.time}</Text>}
          <Text selectable style={[styles.rowTitle, { color: colors.primary }]}>{item.title}</Text>
          {!!item.detail && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{item.detail}</Text>}
          {!!item.location && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{item.location}</Text>}
          {!!item.travel && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{item.travel}</Text>}
          {!!item.owner && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>Owner: {item.owner}</Text>}
          <View style={styles.rowActions}>
            {!!item.recipe && <SmallButton label={expanded ? 'Close recipe' : 'View recipe'} expanded={expanded} onPress={() => setExpanded(value => !value)} colors={colors} icon="book-open" />}
            {!!item.location && kind === 'itinerary' && <SmallButton label="Open map" onPress={() => openLink(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.location)}`)} colors={colors} icon="location-dot" />}
            {!!item.url && <SmallButton label="View source" onPress={() => openLink(item.url)} colors={colors} icon="arrow-up-right-from-square" />}
          </View>
        </View>
      </View>
      {expanded && item.recipe && <RecipeDetail recipe={item.recipe} colors={colors} />}
    </View>
  );
}

function Comparison({ spec, colors, openLink }) {
  return (
    <View>
      <View style={styles.options}>
        {spec.options.map(option => {
          const recommended = spec.recommendation?.optionId === option.id;
          return <View key={option.id} style={[styles.option, { borderColor: recommended ? colors.accent : colors.border, backgroundColor: recommended ? colors.band : 'transparent' }]}>
            {recommended && <Text selectable style={[styles.mealLabel, { color: colors.accent }]}>BEST FIT</Text>}
            <Text selectable style={[styles.optionTitle, { color: colors.primary }]}>{option.title}</Text>
            {!!option.detail && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{option.detail}</Text>}
            {spec.criteria.map((criterion, i) => <View key={criterion} style={[styles.criterion, { borderColor: colors.border }]}>
              <Text selectable style={[styles.criterionLabel, { color: colors.secondary }]}>{criterion}</Text>
              <Text selectable style={[styles.criterionValue, { color: colors.primary }]}>{option.values[i]}</Text>
            </View>)}
            {option.pros.length > 0 && <View style={styles.tradeoffs}><Text selectable style={[styles.criterionLabel, { color: colors.accent }]}>Strengths</Text>{option.pros.map((pro, i) => <Text selectable key={i} style={[styles.rowDetail, { color: colors.primary }]}>{pro}</Text>)}</View>}
            {option.cons.length > 0 && <View style={styles.tradeoffs}><Text selectable style={[styles.criterionLabel, { color: colors.secondary }]}>Tradeoffs</Text>{option.cons.map((con, i) => <Text selectable key={i} style={[styles.rowDetail, { color: colors.secondary }]}>{con}</Text>)}</View>}
            {!!option.url && <SmallButton label="View source" onPress={() => openLink(option.url)} colors={colors} icon="arrow-up-right-from-square" />}
          </View>;
        })}
      </View>
      {spec.recommendation && <GuideTips title="Why this fits" tips={[spec.recommendation.reason]} colors={colors} />}
    </View>
  );
}

function Practice({ questions, colors }) {
  const [answers, setAnswers] = useState({});
  return questions.map((question, index) => {
    const answer = answers[question.id];
    return <View key={question.id} style={[styles.question, { borderColor: colors.border }]}>
      <Text selectable style={[styles.mealLabel, { color: colors.accent }]}>QUESTION {index + 1}</Text>
      <Text selectable style={[styles.rowTitle, { color: colors.primary }]}>{question.question}</Text>
      <View accessibilityRole="radiogroup" accessibilityLabel={question.question}>
        {question.choices.map((choice, i) => <Pressable key={i} accessibilityRole="radio" aria-checked={answer === i} accessibilityLabel={choice} accessibilityState={{ checked: answer === i }}
          onPress={() => setAnswers(previous => ({ ...previous, [question.id]: i }))}
          {...(Platform.OS === 'web' && { tabIndex: (answer ?? 0) === i ? 0 : -1, onKeyDown: event => selectionKeys(event, next => setAnswers(previous => ({ ...previous, [question.id]: next })), i, question.choices.length, 'radio') })}
          style={({ pressed }) => [styles.choice, { borderColor: answer === i ? colors.accent : colors.border, backgroundColor: answer === i ? colors.band : 'transparent', opacity: pressed ? 0.7 : 1 }]}>
          <Text style={[styles.rowDetail, { color: colors.primary }]}>{choice}</Text>
        </Pressable>)}
      </View>
      {answer !== undefined && <View accessibilityLiveRegion="polite" style={[styles.feedback, { backgroundColor: colors.band }]}>
        <Text selectable style={[styles.rowTitle, { color: colors.primary }]}>{answer === question.answer ? 'That’s right.' : 'Not quite. Try again.'}</Text>
        {answer === question.answer && !!question.explanation && <Text selectable style={[styles.rowDetail, { color: colors.secondary }]}>{question.explanation}</Text>}
      </View>}
    </View>;
  });
}

function ExperienceCard({ spec, colors: themeColors, conversationId, messageId }) {
  const captureTargetRef = useRef(null);
  const presentation = EXPERIENCE_PRESENTATION[spec.kind];
  const colors = useMemo(() => ({ ...getGuideColors(themeColors, spec.kind), accent: presentation[themeColors.status_bar_style === 'dark' ? 'light' : 'dark'] }), [themeColors, presentation, spec.kind]);
  const userId = useAuthStore(state => state.user?.id);
  const canPersist = useChatStore(state => {
    const chat = findChatById(state.chats, conversationId);
    return chat?.isOwner === true && !chat?.flags?.isTemporary;
  });
  const addToast = useToastStore(state => state.actions.addToast);
  const progress = useExperienceProgress({ spec, userId, conversationId, messageId, canPersist: canPersist && !!messageId });
  const [tab, setTab] = useState('plan');
  const tabs = [{ key: 'plan', label: spec.kind === 'mealplan' ? 'Meals' : 'Plan' }];
  if (spec.supplies.length) tabs.push({ key: 'supplies', label: spec.kind === 'mealplan' ? 'Shopping list' : 'Supplies' });
  if (spec.quiz.length) tabs.push({ key: 'practice', label: 'Practice' });
  const currentTab = tabs.some(item => item.key === tab) ? tab : 'plan';
  const openLink = async url => {
    try { await Linking.openURL(url); } catch (_) { addToast('Could not open this link.', 'error'); }
  };
  let rowNumber = 0;
  return (
    <View style={[guideStyles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <View ref={captureTargetRef} collapsable={false} dataSet={{ guideKind: spec.kind, cardCapture: '', experience: spec.kind }} style={[guideStyles.captureTarget, { backgroundColor: colors.background }]}>
        <GuideHeader kind={presentation.label} icon={presentation.icon} title={spec.title} description={spec.description} colors={colors} />
        <GuideSummary items={spec.summary} details={[]} colors={colors} />
        {tabs.length > 1 && <View accessibilityRole="tablist" style={[styles.tabs, { backgroundColor: colors.band }]}>
          {tabs.map((item, index) => <Pressable key={item.key} accessibilityRole="tab" aria-selected={currentTab === item.key} accessibilityState={{ selected: currentTab === item.key }} onPress={() => setTab(item.key)}
            {...(Platform.OS === 'web' && { tabIndex: currentTab === item.key ? 0 : -1, onKeyDown: event => selectionKeys(event, next => setTab(tabs[next].key), index, tabs.length, 'tab') })}
            style={[styles.tab, { backgroundColor: currentTab === item.key ? colors.background : 'transparent' }]}>
            <Text style={[styles.tabText, { color: currentTab === item.key ? colors.primary : colors.secondary }]}>{item.label}</Text>
          </Pressable>)}
        </View>}
        {progress.total > 0 && <View style={styles.progress}>
          <Text selectable style={[styles.progressText, { color: colors.secondary }]}>{progress.completed} of {progress.total} {spec.kind === 'mealplan' ? 'groceries checked' : 'complete'}</Text>
          <View accessibilityRole="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.completed} accessibilityLabel="Plan completion" accessibilityValue={{ min: 0, max: progress.total, now: progress.completed }} style={[styles.track, { backgroundColor: colors.border }]}>
            <View style={[styles.fill, { backgroundColor: colors.accent, width: `${100 * progress.completed / progress.total}%` }]} />
          </View>
        </View>}
        {currentTab === 'supplies' ? spec.supplies.map((group, index) => <View key={index}>
          <GuideSection title={group.title} colors={colors} />
          {group.items.map(item => <CheckRow key={item.id} item={item} colors={colors} progress={progress} />)}
        </View>) : currentTab === 'practice' ? <Practice key={JSON.stringify(spec.quiz)} questions={spec.quiz} colors={colors} /> : <View>
          {!!spec.equipment.length && <GuideTips title="What you’ll need" tips={[spec.equipment.join(' · ')]} colors={colors} />}
          {spec.kind === 'comparison' ? <><GuideSection title={presentation.section} colors={colors} /><Comparison spec={spec} colors={colors} openLink={openLink} /></> : spec.sections.map((section, index) => <View key={index}>
            <GuideSection title={section.title} colors={colors} />
            {section.items.map(item => ['checklist', 'studyplan', 'projectguide'].includes(section.style)
              ? <CheckRow key={item.id} item={item} colors={colors} progress={progress} />
              : <PlanRow key={item.id} item={item} index={rowNumber++} kind={spec.kind} colors={colors} openLink={openLink} />)}
          </View>)}
          <GuideTips title="Keep in mind" tips={spec.tips} colors={colors} />
        </View>}
        {progress.total > 0 && <Text selectable accessibilityLiveRegion="polite" style={[styles.persistence, { color: colors.secondary }]}>
          {progress.failed ? 'Progress could not be saved on this device.' : !progress.ready ? 'Loading progress…' : progress.persistent ? 'Progress saved on this device.' : 'Progress is kept while this card is open.'}
        </Text>}
      </View>
      <CardActions conversationId={conversationId} messageId={messageId} captureTargetRef={captureTargetRef} exportTitle={spec.title} accentColor={colors.accent} borderColor={colors.border} bandColor={colors.band} textColor={colors.secondary} />
    </View>
  );
}
ExperienceCard.propTypes = { spec: PropTypes.object.isRequired, colors: PropTypes.object.isRequired, conversationId: PropTypes.string, messageId: PropTypes.string };
const styles = StyleSheet.create({
  tabs: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, padding: 4, borderRadius: 13, marginTop: 22 },
  tab: { flexGrow: 1, flexBasis: 80, minHeight: 44, justifyContent: 'center', alignItems: 'center', borderRadius: 10, padding: 8 },
  tabText: { fontSize: 13, fontWeight: '600' },
  progress: { marginTop: 20 },
  progressText: { fontSize: 12, lineHeight: 18 },
  track: { height: 3, borderRadius: 2, overflow: 'hidden', marginTop: 9 },
  fill: { height: 3, borderRadius: 2 },
  checkRow: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 13, minHeight: 48, borderBottomWidth: 1 },
  checkbox: { width: 19, height: 19, borderWidth: 1, borderRadius: 6, marginRight: 12, marginTop: 2, alignItems: 'center', justifyContent: 'center' },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: 15, fontWeight: '600', lineHeight: 23 },
  rowDetail: { fontSize: 13, lineHeight: 20, marginTop: 4 },
  planRow: { paddingVertical: 14, borderBottomWidth: 1 },
  rowMain: { flexDirection: 'row', alignItems: 'flex-start' },
  timeColumn: { width: 78, paddingRight: 12, alignSelf: 'stretch' },
  time: { fontSize: 12, lineHeight: 18, fontWeight: '600', fontVariant: ['tabular-nums'] },
  timelineLine: { width: 1, flexGrow: 1, minHeight: 15, marginTop: 10, marginLeft: 3 },
  rowActions: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 12 },
  smallButton: { flexDirection: 'row', alignItems: 'center', minHeight: 44, paddingHorizontal: 4, borderRadius: 8, gap: 7 },
  smallButtonText: { fontSize: 12, lineHeight: 18, fontWeight: '600', flexShrink: 1 },
  mealLabel: { fontSize: 11, lineHeight: 18, fontWeight: '600', letterSpacing: 0.7, marginBottom: 4 },
  recipeDetail: { padding: 16, borderRadius: 14, marginTop: 12 },
  recipeLine: { fontSize: 13, lineHeight: 21, marginBottom: 8 },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  option: { flexGrow: 1, flexBasis: 210, minWidth: 0, borderWidth: 1, borderRadius: 16, padding: 16 },
  optionTitle: { fontSize: 18, lineHeight: 25, fontWeight: '600', letterSpacing: -0.3 },
  criterion: { borderTopWidth: 1, paddingTop: 10, marginTop: 12 },
  criterionLabel: { fontSize: 11, lineHeight: 18, fontWeight: '600' },
  criterionValue: { fontSize: 14, lineHeight: 21, marginTop: 3 },
  tradeoffs: { marginTop: 16 },
  question: { paddingVertical: 20, borderBottomWidth: 1 },
  choice: { borderWidth: 1, borderRadius: 10, minHeight: 44, justifyContent: 'center', padding: 10, marginTop: 8 },
  feedback: { padding: 14, borderRadius: 12, marginTop: 12 },
  persistence: { fontSize: 11, lineHeight: 18, marginTop: 18 },
});
export default React.memo(ExperienceCard);
